/* eslint-disable complexity */
import { convertDDMMYYYtoYYYYMMDD } from '@futrli/predict/utils/dates';
import {
  getCustomYearsWindowSpec,
  getRollingForecastWindowSpec
} from '@futrli/predict/forecast/create-forecast/get-forecast-window-spec';
import { getForecastFromDateWithinFixedWindow } from '@futrli/predict/forecast/create-forecast/get-fixed-forecast-window-payload';

import { ForecastToUpdate, UpdateForecastFormType, UpdateForecastRequestData } from '../types';

interface GetFixedForecastRequestDataParams {
  duration: string;
  from: string;
  initialDuration: string;
  initialForecastFromDate: string;
  initialFrom: string;
  initialName: string;
  name: string;
  isBudget?: boolean;
}

interface GetIsDirtyParams {
  duration: string;
  formType: UpdateForecastFormType;
  from: string;
  initialDuration: string;
  initialFrom: string;
  initialName: string;
  initialPreviousActuals: boolean;
  name: string;
  previousActuals: boolean;
}

interface GetRollingForecastRequestDataParams {
  duration: string;
  initialDuration: string;
  initialName: string;
  initialPreviousActuals: boolean;
  name: string;
  previousActuals: boolean;
}

/**
 * Builds a name field only when its editable value changed.
 *
 * @param name - Current name entered in the form.
 * @param initialName - Name returned with the loaded forecast.
 * @returns A name-only PATCH fragment, or an empty fragment when unchanged.
 */
const getChangedNamePayload = (name: string, initialName: string): Pick<UpdateForecastRequestData, 'name'> =>
  name !== initialName ? { name } : {};

/**
 * Reads the editable duration value from a forecast window specification.
 *
 * @param forecast - Forecast list item being edited.
 * @param formType - Forecast edit form variant derived from the forecast.
 * @returns A fixed-year value, an `FY+N` value, or an empty value when metadata is unavailable.
 */
export const getDurationFromWindowSpec = (forecast: ForecastToUpdate, formType: UpdateForecastFormType): string => {
  const { window_spec: windowSpec } = forecast;

  if (formType === 'budget') {
    return '1';
  }

  if (formType === 'fixed' && windowSpec?.kind === 'custom-years') {
    return typeof windowSpec.duration_years === 'number' ? String(windowSpec.duration_years) : '';
  }

  // This is a temporary workaround for a bug in the API that returns `full-financial-years-with-actuals` for some fixed forecasts.
  // Those are forecasts that are created with old screens. The correct value should be `custom-years`.
  // If this is the case, we will return `forecast_financial_years + actuals_financial_years + 1` value as the duration.
  if (formType === 'fixed' && windowSpec?.kind === 'full-financial-years-with-actuals') {
    return typeof windowSpec.forecast_financial_years === 'number' &&
      typeof windowSpec.actuals_financial_years === 'number'
      ? String(windowSpec.forecast_financial_years + windowSpec.actuals_financial_years + 1)
      : '';
  }

  if (
    formType === 'rolling' &&
    windowSpec?.kind !== 'custom-years' &&
    typeof windowSpec?.forecast_financial_years === 'number'
  ) {
    return `FY+${windowSpec.forecast_financial_years}`;
  }

  return '';
};

/**
 * Builds the minimal PATCH payload for a budget or manual 3-way forecast.
 *
 * @param params - Current and initial fixed-window form values.
 * @param params.duration - Current duration selected in the form.
 * @param params.from - Current Start date formatted as `dd/MM/yyyy`.
 * @param params.initialDuration - Duration returned with the loaded forecast.
 * @param params.initialForecastFromDate - Existing forecast start formatted as `yyyy-MM-dd`.
 * @param params.initialFrom - Initial form Start date formatted as `dd/MM/yyyy`.
 * @param params.initialName - Name returned with the loaded forecast.
 * @param params.name - Current name entered in the form.
 * @param params.isBudget - Indicates if the forecast is a budget.
 * @returns Only changed PATCH fields, including a complete window specification when duration changed.
 */
export const getFixedForecastRequestData = ({
  duration,
  from,
  initialDuration,
  initialForecastFromDate,
  initialFrom,
  initialName,
  name,
  isBudget
}: GetFixedForecastRequestDataParams): UpdateForecastRequestData => {
  const durationChanged = duration !== initialDuration;
  const windowFromChanged = from !== initialFrom;
  const windowFrom = convertDDMMYYYtoYYYYMMDD(from) ?? '';
  const requestData: UpdateForecastRequestData = {
    ...getChangedNamePayload(name, initialName),
    ...(windowFromChanged ? { window_from: windowFrom } : {}),
    ...(durationChanged ? { window_spec: getCustomYearsWindowSpec(duration) } : {})
  };

  if (durationChanged || windowFromChanged) {
    // If the forecast is a budget, always update the forecast_from_date to match the window_from.
    if (isBudget) {
      requestData.forecast_from_date = windowFrom;
      return requestData;
    }

    const nextForecastFromDate = getForecastFromDateWithinFixedWindow({
      duration,
      forecastFromDate: initialForecastFromDate,
      windowFrom
    });

    if (nextForecastFromDate !== initialForecastFromDate) {
      requestData.forecast_from_date = nextForecastFromDate;
    }
  }

  return requestData;
};

/**
 * Captures the original duration used for semantic dirty-state comparisons.
 *
 * @param forecast - Forecast list item being edited.
 * @param formType - Forecast edit form variant derived from the forecast.
 * @returns The duration stored in the loaded window specification, or an empty value when absent.
 */
export const getInitialDuration = (forecast: ForecastToUpdate, formType: UpdateForecastFormType): string => {
  if (formType === 'budget') {
    return forecast.window_spec?.kind === 'custom-years' && typeof forecast.window_spec.duration_years === 'number'
      ? String(forecast.window_spec.duration_years)
      : '';
  }

  return getDurationFromWindowSpec(forecast, formType);
};

/**
 * Determines whether any editable value differs from the loaded forecast.
 *
 * @param params - Current and initial form values.
 * @param params.duration - Current duration selection.
 * @param params.formType - Active edit form variant.
 * @param params.from - Current Start date.
 * @param params.initialDuration - Initial duration selection.
 * @param params.initialFrom - Initial Start date.
 * @param params.initialName - Initial forecast name.
 * @param params.initialPreviousActuals - Initial previous-actuals selection.
 * @param params.name - Current forecast name.
 * @param params.previousActuals - Current previous-actuals selection.
 * @returns Whether the form has a change that can be submitted.
 */
export const getIsDirty = ({
  duration,
  formType,
  from,
  initialDuration,
  initialFrom,
  initialName,
  initialPreviousActuals,
  name,
  previousActuals
}: GetIsDirtyParams): boolean => {
  const hasNameChanged = name !== initialName;

  if (formType === 'rolling') {
    return hasNameChanged || duration !== initialDuration || previousActuals !== initialPreviousActuals;
  }

  const hasWindowFromChanged = from !== initialFrom;

  if (formType === 'fixed') {
    return hasNameChanged || hasWindowFromChanged || duration !== initialDuration;
  }

  return hasNameChanged || hasWindowFromChanged;
};

/**
 * Builds the minimal PATCH payload for an invoice-based 3-way forecast.
 *
 * @param params - Current and initial rolling-window form values.
 * @param params.duration - Current financial-year duration selection.
 * @param params.initialDuration - Duration returned with the loaded forecast.
 * @param params.initialName - Name returned with the loaded forecast.
 * @param params.initialPreviousActuals - Previous-actuals value returned with the loaded forecast.
 * @param params.name - Current name entered in the form.
 * @param params.previousActuals - Current previous-actuals checkbox value.
 * @returns Only changed PATCH fields, with the complete rolling window specification when required.
 */
export const getRollingForecastRequestData = ({
  duration,
  initialDuration,
  initialName,
  initialPreviousActuals,
  name,
  previousActuals
}: GetRollingForecastRequestDataParams): UpdateForecastRequestData => {
  const changedNamePayload = getChangedNamePayload(name, initialName);
  const windowSpecChanged = duration !== initialDuration || previousActuals !== initialPreviousActuals;

  if (windowSpecChanged) {
    return {
      ...changedNamePayload,
      window_spec: getRollingForecastWindowSpec({ duration, previousActuals })
    };
  }

  return changedNamePayload;
};
